/**
 * The rig body from lane C0-CHAR (user-approved 2026-10-10, rounds 1–4; research docs/research/C0_CHAR_MODEL.md, review
 * docs/review/c0-char/): Model 0's proportions on C0's head, shaped limbs, deltoids, longer feet, a quiet bare-torso
 * form, profile hips, the idle stance with the arms down, and hands in one pixel style (hanging mitten, fist/grip,
 * open palm, sword fingers) instead of the capsule hands of hands.ts. Every rig figure uses it: `newSkeleton` on the
 * pose (rig/sets.ts `frameOf`), `newBodyLayer` for the body (`appearanceOf`), and the default drawers and briefs
 * (underwear.ts). Grown from the lab clone (tools/charLab/labBody.ts), every fix switched on.
 */
import { shade } from '../palette';
import {
  blob,
  curveSpans,
  type Layer,
  limb,
  type Piece,
  px,
  RIG_RES,
  toward,
  torso,
  torsoEdges,
  torsoWidths,
  lerp,
  zOf,
} from './figure';
import { drawHand, drawHandArt, handShape, handSprite, type HandSprite, handTones } from './hands';
import { HEAD } from './heads';
import { GROUND, type Side, type Skeleton, type Joint } from './skeleton';

type LabFix = 'shoulders' | 'limbs' | 'feet' | 'torso' | 'stance' | 'hair' | 'hands' | 'shorts' | 'anatomy';

type G = CanvasRenderingContext2D;
type Pt = { x: number; y: number };

function mix(a: string, b: string, k: number): string {
  const ch = (h: string, i: number) => parseInt(h.slice(i, i + 2), 16);
  return `#${[1, 3, 5]
    .map((i) =>
      Math.round(ch(a, i) * (1 - k) + ch(b, i) * k)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

const outerSide = (dx: number): number => (dx < -0.5 ? -1 : dx > 0.5 ? 1 : 0);

/**
 * figure.ts `limb`'s `outer` for an arm: its straight-outer-edge alignment shifts the narrowing forearm sideways,
 * which suits an arm hanging down but, on an arm held out level, opens a gap between the forearm's end and the hand.
 * Only a mostly vertical forearm keeps it.
 */
function armOuter(sk: Skeleton, side: Side): number {
  const el = sk.joints[`elbow${side}`];
  const wr = sk.joints[`wrist${side}`];
  const vertical = Math.abs(wr.y - el.y) > Math.abs(wr.x - el.x) * 1.5;
  return vertical ? outerSide(sk.joints[`shoulder${side}`].x - sk.joints.chest.x) : 0;
}

// ── stance (a skeleton tweak, applied before the layers are drawn) ──

/**
 * The standing pose with the arms hanging nearer the thighs (hands not flared out from the hips)
 * and, head-on, the weight on one leg: the hips shift a pixel over the standing leg, the free knee
 * comes in and its foot turns a little out. Only the idle pose is touched.
 */
function labSkeleton(sk: Skeleton, fixes: readonly LabFix[]): Skeleton {
  const stance = fixes.includes('stance') && sk.pose === 'idle';
  const seat = fixes.includes('limbs');
  const anat = fixes.includes('anatomy');
  if (!stance && !seat && !anat) return sk;
  const j = Object.fromEntries(Object.entries(sk.joints).map(([k, v]) => [k, { ...v }])) as Record<
    keyof Skeleton['joints'],
    Joint
  >;
  // A natural seat curve on the male body (rig default 0): the hips round out behind in profile.
  let shape = seat ? { ...sk.shape, seat: Math.max(sk.shape.seat ?? 0, 1) } : sk.shape;
  if (anat) shape = anatomy(j, sk, shape);
  if (stance) {
    const cx = j.pelvis.x;
    // Hands clasped in front (a woman's bearing) stay as the rig holds them.
    for (const s of sk.resting === 'front' && !anat ? [] : (['A', 'B'] as Side[])) {
      const sh = j[`shoulder${s}`];
      const sign = Math.sign(sh.x - cx) || (s === 'A' ? 1 : -1);
      // A person's own stance with the hands somewhere else (on the hips, behind the back, on the hilt) keeps it:
      // only hanging or clasped hands are let down to the sides.
      const own = handShape(sk, s, false);
      if (anat && ((own !== 'relaxed' && own !== 'clasp') || sk.resting === 'back')) continue;
      if (anat) {
        // As Model 0 stands: the arm hangs nearly straight from the shoulder, the elbow a touch out at the
        // waist, the wrist at the crotch beside the thigh (upper arm 9.5 px, forearm 11.5). A woman's clasped-hands
        // bearing stands this way too in the lab (Model 0's stance), until the clasp has its own hands.
        // Head-on the hand clears the hip by a pixel, so it keeps its own outline.
        const fem = sk.shape.bust ? 0.6 : 0;
        const out = sk.view === 'side' ? -0.6 : sign * ((sk.square ? 1.5 : 0.8) + fem);
        const out2 = sk.view === 'side' ? 0.6 : sign * (sk.square ? 1.2 : 0.4);
        const el = j[`elbow${s}`];
        const wr = j[`wrist${s}`];
        const hd = j[`hand${s}`];
        el.x = sh.x + out;
        el.y = sh.y + 9.5;
        wr.x = el.x + out2;
        wr.y = el.y + 11.5;
        hd.x = wr.x + out2 * 0.5;
        hd.y = wr.y + 2.5;
      } else if (sk.square || sk.view === 'front3' || sk.view === 'back3') {
        // Hands toward the thighs: the wrist 2 px in, the elbow 1 px in (its bend kept).
        j[`elbow${s}`].x -= sign * 1;
        j[`wrist${s}`].x -= sign * 2;
        j[`hand${s}`].x -= sign * 2;
      }
    }
    if (sk.square) {
      // Symmetric: both feet planted on one row, mirrored about the hips; knees over the feet.
      const y = Math.max(j.ankleA.y, j.ankleB.y);
      // A woman's feet a pixel closer together than a man's.
      const half = Math.max(
        sk.shape.bust ? 2 : 3,
        Math.round(Math.abs(j.hipA.x - j.hipB.x) / 2) + (sk.shape.bust ? 0 : 1),
      );
      for (const s of ['A', 'B'] as Side[]) {
        const sign = Math.sign(j[`hip${s}`].x - cx) || (s === 'A' ? 1 : -1);
        j[`ankle${s}`].y = y;
        j[`ankle${s}`].x = cx + sign * half;
        j[`knee${s}`].x = cx + sign * half;
        j[`hip${s}`].y = j.pelvis.y;
      }
    } else if (sk.view === 'side') {
      // Both hips under the pelvis, so the thigh roots stay inside the hip line front and back.
      j.hipA.x = j.hipB.x = j.pelvis.x;
      // The far arm hangs straight behind the near one (no elbow showing behind the back). Facing left
      // (rig/dir8.ts) the far arm is the right one (A).
      const [far, near]: Side[] = sk.left ? ['A', 'B'] : ['B', 'A'];
      for (const n of ['shoulder', 'elbow', 'wrist', 'hand'] as const) {
        j[`${n}${far}`].x = j[`${n}${near}`].x - 0.5;
        j[`${n}${far}`].y = j[`${n}${near}`].y;
      }
    } else if (sk.view === 'back3') {
      // From behind and turned the rig's idle opens into a wide stride: the feet brought in under the hips.
      const far: Side = j.hipA.z < j.hipB.z ? 'A' : 'B';
      const near: Side = far === 'A' ? 'B' : 'A';
      j[`hip${far}`].y = j[`hip${near}`].y = j.pelvis.y;
      for (const s of ['A', 'B'] as Side[]) {
        const hx = j[`hip${s}`].x;
        j[`knee${s}`].x = hx + (j[`knee${s}`].x - hx) * 0.4;
        j[`ankle${s}`].x = hx + (j[`ankle${s}`].x - hx) * 0.4;
      }
      j[`ankle${far}`].y = j[`ankle${near}`].y - 1;
    } else if (sk.view === 'front3') {
      // Turned: the hips level and the far leg set a step back, so both legs and feet show.
      const far: Side = j.hipA.z < j.hipB.z ? 'A' : 'B';
      const near: Side = far === 'A' ? 'B' : 'A';
      j[`hip${far}`].y = j[`hip${near}`].y = j.pelvis.y;
      j[`knee${far}`].x -= 1;
      j[`ankle${far}`].x -= 2;
      j[`ankle${far}`].y = j[`ankle${near}`].y - 1;
    }
  }
  if (sexBody && anat && (sk.pose === 'idle' || sk.pose === 'ready')) sexStance(j, sk);
  // The lab's idle stands with the arms at the sides for both sexes (see above).
  const resting = anat && stance && sk.resting === 'front' ? 'side' : sk.resting;
  return { ...sk, joints: j, shape, resting };
}

/** The body differences between the sexes below (on in the game; review tools turn them off for a before). */
let sexBody = true;
export function setSexBody(v: boolean): void {
  sexBody = v;
}

/**
 * The sexes stand apart (lane C0-OUTFIT2X, user 2026-10-10): at ease and on guard a man plants his feet wider and lets
 * his arms hang a little off the body; a woman stands with her feet closer, the knees drawn in, the arms near the body.
 * Leg and arm joints move about the pelvis and the shoulders only, so the bones keep their lengths near enough.
 */
function sexStance(j: Record<keyof Skeleton['joints'], Joint>, sk: Skeleton): void {
  const female = !!sk.shape.bust;
  const cx = j.pelvis.x;
  const turned = sk.view === 'side' ? 0.35 : sk.square ? 1 : 0.7;
  const legs = female ? 0.75 : 1.25;
  for (const s of ['A', 'B'] as Side[]) {
    const hip = j[`hip${s}`];
    const knee = j[`knee${s}`];
    const ankle = j[`ankle${s}`];
    const k = 1 + (legs - 1) * turned;
    // Feet spread (or drawn in) about the pelvis; a woman's knees in a touch more than her feet.
    ankle.x = cx + (ankle.x - cx) * k;
    knee.x = cx + (knee.x - cx) * (female ? k * 0.9 : k);
    hip.x = cx + (hip.x - cx) * (female ? 1 : 1.05);
    if (sk.pose === 'idle' && !(sk.resting === 'front' || sk.resting === 'back')) {
      const sh = j[`shoulder${s}`];
      const sign = Math.sign(sh.x - cx) || (s === 'A' ? 1 : -1);
      const out = (female ? -0.35 : 0.5) * (sk.view === 'side' ? 0 : 1);
      for (const n of ['elbow', 'wrist', 'hand'] as const) j[`${n}${s}`].x += sign * out * (n === 'elbow' ? 0.6 : 1);
    }
  }
}

/**
 * Body proportions from Model 0 (ref3d.ts; measured with Model 0 standing, scaled to the rig's neck-to-sole height,
 * docs/research/C0_CHAR_MODEL.md §5). Against it the rig body (grown, idle, art px from the soles, neck at 67) has
 * its shoulders 8 px low (57 vs 65) and 2 px too wide each side, the elbows 8 low, the wrists 6 low, the hips 6 low
 * (38 vs 44) and the knees 5 low (21 vs 26): a long torso on short legs with low-hung arms. Applied to every pose as
 * bone-length and offset changes, so each pose keeps its own action:
 * - legs: thighs ×1.06 and shins ×1.3 from the hips; the pelvis and legs then rise so the lowest foot stays where it
 *   was (the head does not move: the torso gets shorter, the legs longer);
 * - the trunk's top (chest) 2 px higher, so the neck is shorter, and the shoulders 2.5 px higher and in toward the
 *   neck (1.8 px a man, 1.6 a woman), the arms with them (2.5 px up); the upper arm ×0.95, so the elbow comes
 *   to the waist and the wrist to the crotch, as on Model 0;
 * - the trunk's shoulder half-width a little less (−0.9 a man, −0.6 a woman), to match the narrower shoulder joints.
 */
function anatomy(
  j: Record<keyof Skeleton['joints'], Joint>,
  sk: Skeleton,
  shape: Skeleton['shape'],
): Skeleton['shape'] {
  const female = !!sk.shape.bust;
  const ground = Math.max(j.ankleA.y, j.ankleB.y);
  for (const s of ['A', 'B'] as Side[]) {
    const hip = j[`hip${s}`];
    const knee = j[`knee${s}`];
    const ankle = j[`ankle${s}`];
    const k2 = { x: hip.x + (knee.x - hip.x) * 1.06, y: hip.y + (knee.y - hip.y) * 1.06 };
    ankle.x = k2.x + (ankle.x - knee.x) * 1.3;
    ankle.y = k2.y + (ankle.y - knee.y) * 1.3;
    knee.x = k2.x;
    knee.y = k2.y;
  }
  const dy = Math.max(j.ankleA.y, j.ankleB.y) - ground;
  for (const n of ['pelvis', 'hipA', 'hipB', 'kneeA', 'kneeB', 'ankleA', 'ankleB'] as const) j[n].y -= dy;
  // The waist: a man's 4 px over the pelvis; a woman's at the navel, 7 px over it and a little narrower, so her
  // torso narrows from the bust to the waist and flares to the hips (Model 0's curve) instead of a straight column.
  j.waist.y = j.pelvis.y - (female ? 7 : 4);
  j.chest.y -= 2;
  // A woman's shoulder joints come in less: her arms must hang clear of the waist so its curve shows.
  // A man's broader (lane C0-OUTFIT2X, user: the sexes apart by silhouette): his come in only 0.8, a woman's 0.6.
  const narrow = female ? 0.6 : sexBody ? 0.2 : 1.8;
  for (const s of ['A', 'B'] as Side[]) {
    const sh = j[`shoulder${s}`];
    const sign = Math.sign(sh.x - j.chest.x) || (s === 'A' ? -1 : 1);
    // Head-on the shoulders come in; turned and in profile the depth hides most of it.
    const dx = -sign * narrow * (sk.square ? 1 : 0.4);
    // Hands clasped in front (a woman's bearing) keep their height: only the shoulders rise.
    for (const n of ['shoulder', 'elbow', 'wrist', 'hand'] as const) {
      j[`${n}${s}`].x += dx;
      if (n === 'shoulder' || sk.resting !== 'front') j[`${n}${s}`].y -= 2.5;
    }
    const el = j[`elbow${s}`];
    const e2 = { x: sh.x + (el.x - sh.x) * 0.95, y: sh.y + (el.y - sh.y) * 0.95 };
    for (const n of ['wrist', 'hand'] as const) {
      j[`${n}${s}`].x += e2.x - el.x;
      j[`${n}${s}`].y += e2.y - el.y;
    }
    el.x = e2.x;
    el.y = e2.y;
  }
  return {
    ...shape,
    shoulder: shape.shoulder + (female ? -0.6 : sexBody ? 1.1 : -0.9),
    waist: shape.waist - (female ? 0.4 : 0),
  };
}

// ── the body ──

interface Skin {
  skin: string;
  skinS: string;
}

/**
 * The shaped limb: [from, to, w0, w1] runs, as figure.ts `limb` takes them, for an arm or a leg
 * with its muscles in. `t` points along the bone place the bulges.
 */
function armParts(
  sh: Pt,
  el: Pt,
  wr: Pt,
  L: number,
  shaped: boolean,
  toWrist = false,
): [Pt, Pt, number, number][] {
  // The rig stops the forearm short of the wrist for its capsule hands; the mitten starts at the wrist.
  const end = toward(wr, el, toWrist ? 0.5 : 1.5);
  if (!shaped)
    return [
      [sh, el, 4 + L, 3 + L],
      [el, end, 3 + L * 0.5, 3 + L * 0.5],
    ];
  const fore = lerp(el, end, 0.35);
  return [
    [sh, lerp(sh, el, 0.45), 5 + L, 4 + L],
    [lerp(sh, el, 0.45), el, 4 + L, 3 + L * 0.5],
    [el, fore, 3 + L * 0.5, 4 + L * 0.3],
    [fore, end, 4 + L * 0.3, 2.2],
  ];
}

/**
 * A bare thigh's width where it leaves the hip (art px): its own, or wide enough that its outer edge meets the hip line
 * (BODY-B3-R1: a woman's full hips ran out over thin thighs as a shelf, the briefs flaring past them). In profile its own.
 */
export function thighTop(sk: Skeleton, side: Side): number {
  const own = 5.6 + sk.shape.limb;
  if (sk.view === 'side') return own;
  const hhw = torsoWidths(sk)[2];
  const off = Math.abs(sk.joints[`hip${side}`].x - sk.joints.pelvis.x);
  return Math.max(own, Math.min(own + 3, 2 * (hhw - off) + 0.5));
}

/**
 * The shaped leg (BODY-B3-R1 round 2): the thigh tapering to a knee notch, the calf bulging below it and
 * tapering to a slim ankle. In profile (`back` 1: facing +x) the calf sits behind the shin line, so the shin stays
 * straight in front and the calf rounds out behind.
 */
function legParts(hip: Pt, knee: Pt, ankle: Pt, L: number, shaped: boolean, back = 0): [Pt, Pt, number, number][] {
  if (!shaped)
    return [
      [hip, knee, 5 + L, 4 + L],
      [knee, ankle, 4 + L, 3 + L],
    ];
  const mid = lerp(hip, knee, 0.5);
  const at = lerp(knee, ankle, 0.3);
  const calf = { x: at.x - back * 0.8, y: at.y };
  return [
    [hip, mid, 5.6 + L, 5 + L],
    [mid, knee, 5 + L, 3.8 + L],
    [knee, calf, 3.8 + L, 4.6 + L],
    [calf, ankle, 4.6 + L, 2.6 + L * 0.5],
  ];
}

/**
 * A bare leg head-on or from behind (BODY-B3 R3 M1): the thigh keeps its straight outer edge down to a knee a pixel and more
 * narrower; below the knee the shin is drawn centred, so the calf swells on both edges (the outer one too, where it shows
 * against the background) and tapers to the ankle. The shin starts where the thigh's knee row sits, and ends on the
 * ankle joint, so the foot stays where it was.
 */
function shapedLeg(g: G, hip: Pt, knee: Pt, ankle: Pt, L: number, outer: number, c: Skin): void {
  const mid = lerp(hip, knee, 0.5);
  limb(
    g,
    [
      [hip, mid, 5.6 + L, 5 + L],
      [mid, knee, 5 + L, 3.6 + L],
    ],
    c.skin,
    c.skinS,
    outer,
  );
  const W = Math.max(1, Math.round(5.6 + L));
  const wk = Math.max(1, Math.round(3.6 + L));
  // limb() puts a narrower row against the outer edge: the knee row's left column relative to the joint's.
  const left = outer < 0 ? -Math.floor(W / 2) : outer > 0 ? -Math.floor(W / 2) + W - wk : -Math.floor(wk / 2);
  const d = left + Math.floor(wk / 2);
  const k2 = { x: knee.x + d, y: knee.y };
  const at = lerp(knee, ankle, 0.32);
  const calf = { x: at.x + d * 0.6, y: at.y };
  limb(
    g,
    [
      [k2, calf, 3.6 + L, 5.2 + L],
      [calf, ankle, 5.2 + L, 3.4 + L * 0.5],
    ],
    c.skin,
    c.skinS,
    0,
  );
}

/**
 * A profile bare leg's form as value steps inside its outline, not silhouette notches (BODY-B3 R4 W2: a shaped
 * silhouette read as a dent): a 2-px shade inside the calf's back edge (the knee's lone pixel read as a blemish, R5-2)
 * (facing +x; left views are drawn so and mirrored). Pixel columns follow figure.ts `limb` (centred, `outer` 0).
 */
function legForm(g: G, knee: Pt, ankle: Pt, L: number, c: Skin): void {
  if (Math.abs(ankle.y - knee.y) < 4) return;
  const at = lerp(knee, ankle, 0.3);
  const wc = Math.max(1, Math.round(4.6 + L));
  const cx = Math.round(at.x - 0.8) - Math.floor(wc / 2) + 1;
  px(g, cx, Math.round(at.y), 1, 2, mix(c.skin, c.skinS, 0.5));
}

/**
 * The upper thigh filled out to `thighTop` where it is wider than the leg's own (a woman's full hips), drawn only where
 * nothing is yet (under the leg already drawn), so the leg's own line, knee and foot stay where they are.
 */
function thighFill(g: G, sk: Skeleton, side: Side, c: Skin, outer: number): void {
  const L = sk.shape.limb;
  const top = thighTop(sk, side);
  if (top <= 5.6 + L + 0.4) return;
  const hip = sk.joints[`hip${side}`];
  const mid = lerp(hip, sk.joints[`knee${side}`], 0.55);
  g.save();
  g.globalCompositeOperation = 'destination-over';
  limb(g, [[hip, mid, top, 5 + L]], c.skin, c.skinS, outer);
  g.restore();
}

/**
 * Longer feet: an instep under the ankle, a heel behind it and toes reaching ahead, the sole in
 * shade and, head-on, a toe line parting the big toe. Lifted feet keep the rig's own rows.
 */
function labFoot(g: G, sk: Skeleton, side: Side, c: Skin): void {
  const an = sk.joints[`ankle${side}`];
  // Head-on at rest the far foot sits a row or two up (depth), not off the ground: drawn planted.
  const lifted = GROUND - 3 - an.y >= (sk.square && sk.pose === 'idle' ? 3 : 1.5);
  const x = Math.round(an.x);
  const y = Math.round(an.y);
  const toeLight = shade(c.skin, 0.04);
  if (sk.square) {
    if (lifted) {
      px(g, x - 2, y, 4, 2, c.skin);
      px(g, x - 1, y + 2, 3, 1, c.skinS);
      return;
    }
    // Ankle 3 wide, then the foot 5 wide and the toes 5 wide with a toe gap; the foot turns a
    // little out on its own side (toes toward the side the leg is on).
    // Mirrored about the body: each foot toes out a pixel on its own side.
    const sign = Math.sign(an.x - sk.joints.pelvis.x) || (side === 'A' ? 1 : -1);
    const out = sign > 0 ? 0 : -1;
    px(g, x - 1, y, 3, 1, c.skin);
    px(g, x - 2 + out, y + 1, 5, 1, c.skin);
    px(g, x - 2 + out, y + 2, 5, 1, toeLight);
    px(g, x - 2 + out, y + 3, 5, 1, c.skinS);
    // The big toe parted from the rest, on the inner side.
    const inner = sign > 0 ? x - 1 + out : x + 1 + out;
    px(g, inner, y + 2, 1, 1, c.skinS);
    return;
  }
  if (lifted) {
    px(g, x - 1, y, 3, 1, c.skin);
    px(g, x, y + 1, 4, 1, c.skin);
    px(g, x + 2, y + 2, 3, 1, c.skinS);
    return;
  }
  // Profile: heel one behind the ankle, instep rising to it, toes three ahead, sole in shade.
  px(g, x - 1, y, 4, 1, c.skin);
  px(g, x - 2, y + 1, 7, 1, c.skin);
  px(g, x - 2, y + 2, 8, 1, c.skin);
  px(g, x + 4, y + 2, 2, 1, toeLight);
  px(g, x - 2, y + 3, 8, 1, c.skinS);
  // The ankle bone.
  px(g, x, y, 1, 1, mix(c.skin, c.skinS, 0.5));
}

/** A rounded deltoid on the shoulder, a pixel wider than the arm's root on its outer side. */
function deltoid(g: G, sk: Skeleton, s: Side, c: Skin, L: number): void {
  const j = sk.joints;
  const sh = j[`shoulder${s}`];
  const el = j[`elbow${s}`];
  const out = Math.sign(sh.x - j.chest.x);
  const r = (5 + L) / 2 + 0.4;
  const at = lerp(sh, el, 0.18);
  blob(g, at.x + out * (sk.square ? 0.6 : 0.2) + 0.5, at.y + 0.5, r, r + 0.4, c.skin);
  // Its underside and the line where it meets the arm.
  if (sk.square || sk.view === 'front3' || sk.view === 'back3') {
    const yy = Math.round(at.y + r);
    px(g, Math.round(at.x + out * 0.6 - 1 + (out > 0 ? 1 : 0)), yy, 2, 1, mix(c.skin, c.skinS, 0.55));
  }
}

/**
 * Form on a bare torso: collarbones, the chest's underline, a faint middle line on the abdomen,
 * the navel, and the side's oblique shade; from behind, the spine and the shoulder blades.
 */
function torsoForm(g: G, sk: Skeleton, c: Skin): void {
  const j = sk.joints;
  const soft = mix(c.skin, c.skinS, 0.55);
  const faint = mix(c.skin, c.skinS, 0.35);
  const lit = shade(c.skin, 0.05);
  const cy = Math.round(j.chest.y);
  const wy = Math.round(j.waist.y);
  if (sk.view === 'side') {
    // The chest's underside and the belly's front edge.
    const [, R] = torsoEdges(sk, 0, cy + 5);
    px(g, R - 2, cy + 5, 2, 1, soft);
    // On the belly's own edge (with the chest further forward than the belly it floated off the body: BODY-B3 R3 M5).
    const [, Rb] = torsoEdges(sk, 0, wy);
    px(g, Rb - 1, wy + 1, 1, 1, faint);
    return;
  }
  // The body's middle line: head-on the chest's centre, turned a step toward the way it faces.
  const turn = sk.square ? 0 : 2;
  const mid = (y: number) => {
    const t = Math.max(0, Math.min(1, (y - cy) / Math.max(1, wy - cy)));
    return Math.round(j.chest.x + (j.waist.x - j.chest.x) * t) + turn;
  };
  const [shL, shR] = torsoEdges(sk, 0, cy + 2);
  const half = Math.max(3, Math.round((shR - shL) / 2) - 2);
  if (sk.back) {
    // Spine and shoulder blades.
    for (let y = cy + 2; y <= wy + 2; y++) px(g, mid(y), y, 1, 1, faint);
    const m = mid(cy + 4);
    px(g, m - half + 1, cy + 5, 3, 1, soft);
    px(g, m + half - 3, cy + 5, 3, 1, soft);
    px(g, m - half + 1, cy + 2, 2, 1, lit);
    px(g, m + half - 2, cy + 2, 2, 1, lit);
    return;
  }
  const m = mid(cy + 5);
  if (sk.shape.bust) {
    // A woman's torso: the bust is the rig's own (paintCurves); only collarbones, the navel and the
    // waist's curve in shade on the far side.
    px(g, m - half + 1, cy + 1, half - 2, 1, lit);
    px(g, m + 2, cy + 1, half - 2, 1, lit);
    px(g, mid(wy + 1), wy + 1, 1, 1, soft);
    for (let y = cy + 9; y <= wy + 1; y++) {
      const [L] = torsoEdges(sk, 0, y);
      px(g, L + 1, y, 1, 1, faint);
    }
    return;
  }
  // Toned down (round 4): only a faint, short underline under each side of the chest, the navel and the far
  // side's oblique, no collarbones or middle line, so the bare chest reads as form, not as drawn lines.
  const lw = Math.max(2, (sk.square ? half - 1 : half - 2) - 1);
  const rw = Math.max(2, (sk.square ? half - 1 : half) - 1);
  px(g, m - lw, cy + 6, lw, 1, faint);
  px(g, m + 1, cy + 6, rw, 1, faint);
  px(g, mid(wy + 1), wy + 1, 1, 1, faint);
  for (let y = cy + 9; y <= wy + 1; y++) {
    const [L] = torsoEdges(sk, 0, y);
    px(g, L + 1, y, 1, 1, faint);
  }
}

/**
 * A woman's slimmer face (lane C0-OUTFIT2X, user 2026-10-10): the jaw drawn in a pixel each side from the cheeks down
 * to a narrower, finer chin; turned and in profile only the jaw's back curve comes in. The skull (where hair and hats
 * sit) is untouched.
 */
const SLIM = new Map<string, readonly string[]>();
function slimHead(view: Skeleton['view']): readonly string[] {
  let rows = SLIM.get(view);
  if (rows) return rows;
  const trim = (row: string, l: boolean, r: boolean) => {
    const a = [...row];
    const first = a.findIndex((ch) => ch !== '.');
    let last = -1;
    for (let i = a.length - 1; i >= 0; i--) if (a[i] !== '.') { last = i; break; }
    if (first < 0 || last - first < 3) return row;
    if (l) a[first] = '.';
    if (r) a[last] = '.';
    return a.join('');
  };
  const src = HEAD.rows[view];
  rows = src.map((row, r) => {
    if (view === 'front') return r >= 14 && r <= 18 ? trim(row, true, true) : row;
    if (view === 'front3') return r >= 14 && r <= 18 ? trim(row, true, r >= 16) : row;
    if (view === 'side') return r >= 15 && r <= 18 ? trim(row, true, false) : row;
    return row;
  });
  SLIM.set(view, rows);
  return rows;
}

/** The traced head stamped on the head joint (a copy of figure.ts `head`). */
function head(g: G, sk: Skeleton, c: Skin): void {
  const h = sk.joints.head;
  const x = Math.round(h.x);
  const y = Math.round(h.y);
  const colours: Record<string, string> = {
    s: c.skin,
    e: c.skinS,
    c: shade(c.skin, 0.05),
    S: mix(c.skin, c.skinS, 0.3),
  };
  (sk.shape.bust && sexBody ? slimHead(sk.view) : HEAD.rows[sk.view]).forEach((row, r) => {
    for (let k = 0; k < row.length; k++) {
      const col = colours[row[k]!];
      if (col) px(g, x - HEAD.ox + k, y - HEAD.oy + r, 1, 1, col);
    }
  });
}

/**
 * Profile hips (fixes 'limbs'): the rig's trunk keeps its full hip width down to the pelvis row, a
 * shelf over the narrower thighs front and back (the "lump" at the seat). Here the rows from the
 * waist down narrow into the thigh tops, with the seat rounding out a pixel behind on the way.
 */
function sideHips(g: G, sk: Skeleton, grow: number, c: string, s: string | null): void {
  const j = sk.joints;
  const y0 = Math.round(j.waist.y);
  const y1 = Math.round(j.pelvis.y) + 2;
  const [Lw, Rw] = torsoEdges(sk, grow, y0);
  const hip = j.hipA;
  const th = (5.6 + sk.shape.limb) / 2 + grow;
  const tL = hip.x - th;
  const tR = hip.x + th;
  for (let y = y0; y <= y1; y++) {
    const t = (y - y0) / Math.max(1, y1 - y0);
    const seat = Math.sin(Math.PI * Math.min(1, t * 1.1)) * (sk.shape.seat ?? 0);
    const L = Math.round(Lw + (tL - Lw) * t * t - seat);
    const R = Math.round(Rw + (tR - Rw) * t * t);
    px(g, L, y, R - L + 1, 1, c);
    if (s) px(g, L, y, 1, 1, s);
  }
}

/**
 * Hand shapes in the mitten's style for every other pose (round 4), replacing the rig's capsule hands
 * (hands.ts): small pixel grids laid along the hand's own line (wrist → hand joint, the way the rig and the
 * weapons aim it), rows down the hand, columns across it, sampled at half steps so a slanted hand has no holes.
 * - fist / grip: a 3×3 block, the folded fingers' row in shade (a weapon's grip passes under it, and the
 *   fingers are laid back over the hilt by the finish, see `Piece.grip`);
 * - palm: an open hand 3 wide and 5 long, the fingertips in shade; held out level (palmPush, qi) it bends at the
 *   wrist so the fingers stand up, heel first, as the rig's open palm pushes;
 * - fingers (剑指): a fist with two fingers out along the line;
 * - relaxed / clasp hanging at a slant: the mitten's grid.
 */
const HAND_GRIDS = {
  fist: ['sss', 'sss', 'eee'],
  palm: ['sss', 'sss', 'sss', 'sss', 'eee'],
  fingers: ['sss', 'sss', '.ss', '.ss', '.ee'],
  mitten: ['sss', 'Sss', 'Sss', 'eee'],
} as const;

function handArt(g: G, sk: Skeleton, side: Side, shape: string, c: Skin): void {
  const j = sk.joints;
  const w = j[`wrist${side}`];
  const h = j[`hand${side}`];
  const e = j[`elbow${side}`];
  // The hand's line: wrist → hand joint (the forearm's line when they coincide).
  let dx = h.x - w.x;
  let dy = h.y - w.y;
  if (Math.hypot(dx, dy) < 0.5) {
    dx = w.x - e.x;
    dy = w.y - e.y;
  }
  let l = Math.max(0.001, Math.hypot(dx, dy));
  let d = { x: dx / l, y: dy / l };
  const grid: readonly string[] =
    shape === 'fist' || shape === 'grip'
      ? HAND_GRIDS.fist
      : shape === 'palm'
        ? HAND_GRIDS.palm
        : shape === 'fingers'
          ? HAND_GRIDS.fingers
          : HAND_GRIDS.mitten;
  if (shape === 'palm' && Math.abs(d.y) < 0.6) {
    // Pushing: the fingers stand up from the heel of the hand.
    d = { x: d.x * 0.35, y: -0.94 };
    l = Math.hypot(d.x, d.y);
    d = { x: d.x / l, y: d.y / l };
  }
  const n = { x: -d.y, y: d.x };
  const colours: Record<string, string> = {
    s: c.skin,
    S: mix(c.skin, c.skinS, 0.5),
    e: c.skinS,
  };
  const cols = grid[0]!.length;
  const centre = (cols - 1) / 2;
  for (let r = 0; r < grid.length; r += 0.5)
    for (let k = 0; k < cols; k += 0.5) {
      const ch = grid[Math.floor(r)]![Math.floor(k)]!;
      const col = colours[ch];
      if (!col) continue;
      const off = Math.floor(k) - centre;
      px(g, w.x + d.x * r + n.x * off, w.y + d.y * r + n.y * off, 1, 1, col);
    }
}

/**
 * A simple mitten hand at art resolution, mirrored left/right by construction: a rounded palm
 * three pixels across hanging along the forearm, the fingertips in shade, and a one-pixel thumb on
 * the side toward the camera (head-on: toward the body's middle; turned: the way it faces).
 */
function mitten(g: G, sk: Skeleton, side: Side, c: Skin): void {
  const j = sk.joints;
  const w = j[`wrist${side}`];
  const e = j[`elbow${side}`];
  const l = Math.max(0.001, Math.hypot(w.x - e.x, w.y - e.y));
  const d = { x: (w.x - e.x) / l, y: (w.y - e.y) / l };
  // A hand hanging nearly straight is drawn straight (no one-pixel stagger down its rows).
  const dx = Math.abs(d.x) < 0.35 ? 0 : d.x;
  const at = (k: number) => ({ x: w.x + dx * k, y: w.y + d.y * k });
  // The mitten as a grid down the forearm's line, row 0 at the wrist: a palm three wide and four long
  // (a pixel wider than the wrist), a shade column on the side away from the light, the fingertips'
  // row in the skin's shade (its corners rounded by Scale2x and the outline), and no rim:
  // the figure's ink outline rings it where it stands out, and skin against the drawers parts by
  // colour. The thumb (`t`, a pixel with its shade under it) only turned and in profile, on the side
  // the figure faces. Head-on and from behind both hands have the same shape, mirrored.
  const turned = !sk.square;
  const grid = ['sss', 'Sss', 'Sss', 'eee'];
  // Turned and in profile the hand often lies over the thigh (skin on skin): its shade side and fingertips
  // go a step deeper there, so it parts from the leg behind it.
  const deep = mix(c.skinS, '#3a1c14', 0.3);
  const colours: Record<string, string> = {
    s: c.skin,
    // In profile the hand hangs over the drawers or the thigh: its shade column and fingertips are a line deep
    // enough to part it from them (a one-pixel separation on the side away from the light and underneath).
    S: sk.view === 'side' ? deep : turned ? c.skinS : mix(c.skin, c.skinS, 0.5),
    e: turned ? deep : c.skinS,
  };
  // Head-on, a hand on the screen's left is the right one's mirror image (its columns read backwards).
  const mirror = sk.square && w.x < j.pelvis.x;
  // figure.ts `limb` keeps an arm's outer edge straight (`outer`), narrowing it on the inner side only, so the
  // wrist's two pixels sit against that edge: the hand lies flush with it and a pixel wider toward the body.
  const outer = armOuter(sk, side);
  const left = outer < 0 ? -3 : outer > 0 ? 1 : -1;
  grid.forEach((row, r) => {
    const p = at(r);
    const x = Math.round(p.x);
    const y = Math.round(p.y);
    const n = row.length;
    for (let k = 0; k < n; k++) {
      const col = colours[row[mirror ? n - 1 - k : k]!];
      if (col) px(g, x + left + k, y, 1, 1, col);
    }
  });
  if (turned && !sk.back) {
    // The thumb: a bump on the facing side at the palm's second row.
    const p = at(1);
    px(g, Math.round(p.x) + left + 3, Math.round(p.y), 1, 1, c.skin);
  }
}

/**
 * The default long drawers (underwear.ts 'long') cut to follow the body: the seat on the torso's own
 * rows (with its curve) and each leg on the shaped thigh, a pixel wider, to just above the knee — no
 * square dabs standing out at the hip front and back.
 */
export function newDrawersLayer(colour: string): Layer {
  const s = shade(colour, -0.1);
  return {
    id: 'under',
    label: 'ชุดชั้นใน',
    pieces(sk) {
      BARE.add(sk);
      const j = sk.joints;
      const L = sk.shape.limb;
      const out: Piece[] = [
        {
          z: zOf(sk, 'chest', 0.011),
          draw: (g) => {
            const top = Math.round(j.waist.y);
            if (sk.view === 'side') {
              sideHips(g, sk, 0.5, colour, null);
              const [l, r] = torsoEdges(sk, 0.5, top);
              px(g, l, top, r - l + 1, 1, s);
              return;
            }
            const bottom = Math.round(j.pelvis.y) + 1;
            for (let y = top; y <= bottom; y++) {
              const [l, r] = torsoEdges(sk, 0.5, Math.min(y, Math.round(j.pelvis.y)));
              px(g, l, y, r - l + 1, 1, colour);
            }
            for (const sp of curveSpans(sk, 0.5, 'seat'))
              if (sp.y >= top) px(g, sp.x0, sp.y, sp.x1 - sp.x0 + 1, 1, sp.shade ? s : colour);
            const [l, r] = torsoEdges(sk, 0.5, top);
            px(g, l, top, r - l + 1, 1, s);
          },
        },
      ];
      for (const side of ['B', 'A'] as Side[]) {
        const hip = j[`hip${side}`];
        const knee = j[`knee${side}`];
        const end = lerp(hip, knee, 0.85);
        out.push({
          z: zOf(sk, `hip${side}`, 0.01),
          draw: (g) => {
            limb(
              g,
              [
                [hip, lerp(hip, knee, 0.5), 6.6 + L, 6 + L],
                [lerp(hip, knee, 0.5), end, 6 + L, 5.2 + L],
              ],
              colour,
              s,
              outerSide(hip.x - j.pelvis.x),
            );
            // The hem.
            const w = Math.round(5.2 + L);
            px(g, Math.round(end.x) - Math.floor(w / 2), Math.round(end.y) + Math.floor(w / 2), w, 1, s);
          },
        });
      }
      return out;
    },
  };
}

/**
 * A woman's default set (underwear.ts 'bandeau'): its chest band kept as drawn, the briefs redrawn to
 * follow the body like `labShortsLayer` — the seat on the torso rows (tapered in profile), the legs
 * cut high on the shaped thigh.
 */
export function newBriefsLayer(orig: Layer, colour: string): Layer {
  const s = shade(colour, -0.1);
  return {
    id: 'under',
    label: 'ชุดชั้นใน',
    pieces(sk, fit) {
      BARE.add(sk);
      const j = sk.joints;
      const L = sk.shape.limb;
      const chestZ = zOf(sk, 'chest', 0.012);
      const out: Piece[] = orig.pieces(sk, fit).filter((p) => Math.abs(p.z - chestZ) < 1e-6);
      const top = Math.round(j.waist.y + (j.pelvis.y - j.waist.y) * 0.45);
      out.push({
        z: zOf(sk, 'chest', 0.011),
        draw: (g) => {
          if (sk.view === 'side') {
            // The tapered profile hips, from the briefs' rise down.
            const y0 = Math.round(j.waist.y);
            const y1 = Math.round(j.pelvis.y) + 2;
            const [Lw, Rw] = torsoEdges(sk, 0.5, y0);
            const th = (5.6 + L) / 2 + 0.5;
            for (let y = top; y <= y1; y++) {
              const t = (y - y0) / Math.max(1, y1 - y0);
              const seat = Math.sin(Math.PI * Math.min(1, t * 1.1)) * (sk.shape.seat ?? 0);
              const l = Math.round(Lw + (j.hipA.x - th - Lw) * t * t - seat);
              const r = Math.round(Rw + (j.hipA.x + th - Rw) * t * t);
              // The waistband's shade on the briefs' own top row (the trunk's full hip width there stood out as a tab).
              px(g, l, y, r - l + 1, 1, y === top ? s : colour);
            }
            return;
          } else {
            // Fitted: a half pixel over the skin at the waistband only, then on the skin's own line, the last row
            // drawn in a pixel each side so the leg openings round into the thighs (no flared corners).
            const last = Math.round(j.pelvis.y) + 1;
            for (let y = top; y <= last; y++) {
              const [l, r] = torsoEdges(sk, y === top ? 0.5 : y === last ? -1 : 0, Math.min(y, Math.round(j.pelvis.y)));
              px(g, l, y, r - l + 1, 1, colour);
              // Its side away from the light in shade, so the cloth rounds with the hip (BODY-B3 R3 M7).
              px(g, l, y, 1, 1, s);
            }
            for (const sp of curveSpans(sk, 0, 'seat'))
              if (sp.y >= top) px(g, sp.x0, sp.y, sp.x1 - sp.x0 + 1, 1, sp.shade ? s : colour);
          }
          const [l, r] = torsoEdges(sk, 0.5, top);
          px(g, l, top, r - l + 1, 1, s);
        },
      });
      for (const side of ['B', 'A'] as Side[]) {
        const hip = j[`hip${side}`];
        const knee = j[`knee${side}`];
        out.push({
          z: zOf(sk, `hip${side}`, 0.01),
          draw: (g) =>
            limb(
              g,
              // On the thigh's own width, so the leg openings fit instead of flaring out.
              [[hip, lerp(hip, knee, 0.14), thighTop(sk, side), thighTop(sk, side) - 0.5]],
              colour,
              s,
              outerSide(hip.x - j.pelvis.x),
            ),
        });
      }
      return out;
    },
  };
}

/**
 * Whether a hand's forearm is hidden (round 4 review): most of the forearm, elbow to wrist, lies under the head or
 * the locks hanging beside it (drawn over every arm) or, for an arm behind the body, under the torso. Its hand is then left out too, instead of
 * floating on its own past the chin or the chest's edge.
 */
function forearmHidden(sk: Skeleton, side: Side, armZ: number): boolean {
  const j = sk.joints;
  const el = j[`elbow${side}`];
  const wr = j[`wrist${side}`];
  const h = j.head;
  const rows = HEAD.rows[sk.view];
  const inHead = (x: number, y: number) => {
    const r = Math.round(y) - (Math.round(h.y) - HEAD.oy);
    const c = Math.round(x) - (Math.round(h.x) - HEAD.ox);
    if (r >= 0 && r < rows.length && c >= 0 && c < rows[r]!.length && rows[r]![c] !== '.') return true;
    // Below the chin, the locks of hair that hang each side of the face (drawn over the arms too).
    const dx = Math.abs(x - h.x);
    return y > h.y + HEAD.oy && y <= h.y + 20 && dx >= 4 && dx <= 10;
  };
  const inTorso = (x: number, y: number) => {
    if (y < j.chest.y || y > j.pelvis.y + 1) return false;
    const [L, R] = torsoEdges(sk, 0, Math.round(y));
    return x >= L && x <= R;
  };
  const underHead = h.z > armZ;
  const underTorso = j.chest.z > armZ;
  let hid = 0;
  const n = 8;
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const x = el.x + (wr.x - el.x) * t;
    const y = el.y + (wr.y - el.y) * t;
    if ((underHead && inHead(x, y)) || (underTorso && inTorso(x, y))) hid++;
  }
  return hid / n >= 0.6;
}

/** The lab's bare body: figure.ts `bodyLayer` with the chosen fixes. */
function labBodyLayer(
  c: Skin,
  fixes: readonly LabFix[],
  bareAlways: boolean,
  armedOf: boolean | ((sk: Skeleton) => boolean) = false,
): Layer {
  const has = (f: LabFix) => fixes.includes(f);
  return {
    id: 'body',
    label: 'ร่างกาย',
    pieces(sk) {
      const j = sk.joints;
      const out: Piece[] = [];
      const L = sk.shape.limb;
      // Bare (the default drawers or briefs on, nothing else): those layers mark the skeleton when their pieces are
      // made, after these, so it is read when the pieces are drawn and sorted, not now.
      const isBare = () => bareAlways || BARE.has(sk);
      const armed = typeof armedOf === 'function' ? armedOf(sk) : armedOf;
      for (const s of ['B', 'A'] as Side[]) {
        const hip = j[`hip${s}`];
        const knee = j[`knee${s}`];
        const ankle = j[`ankle${s}`];
        out.push({
          z: zOf(sk, `hip${s}`),
          draw: (g) => {
            // Head-on and from behind only: turned, the centred shin read as a dent at the knee (r4 review).
            if (has('limbs') && isBare() && sk.square) shapedLeg(g, hip, knee, ankle, L, outerSide(hip.x - j.pelvis.x), c);
            else
              limb(
                g,
                legParts(hip, knee, ankle, L, has('limbs') && isBare(), sk.view === 'side' ? 1 : 0),
                c.skin,
                c.skinS,
                outerSide(hip.x - j.pelvis.x),
              );
            if (has('limbs') && isBare()) thighFill(g, sk, s, c, outerSide(hip.x - j.pelvis.x));
            if (has('limbs') && isBare() && sk.view === 'side') legForm(g, knee, ankle, L, c);
            if (has('feet') && isBare()) labFoot(g, sk, s, c);
            else {
              // The rig's own foot rows (figure.ts `footRows` / `foot`), so shoes still fit.
              const an = ankle;
              const lifted = GROUND - 3 - an.y >= 1.5;
              const rows = sk.square
                ? lifted
                  ? [
                      { x: an.x - 2, y: an.y, w: 4 },
                      { x: an.x - 2, y: an.y + 1, w: 4 },
                      { x: an.x - 1.5, y: an.y + 2, w: 3 },
                    ]
                  : [0, 1, 2].map((r) => ({ x: an.x - 2, y: an.y + r, w: 4 }))
                : lifted
                  ? [
                      { x: an.x - 1, y: an.y, w: 3 },
                      { x: an.x, y: an.y + 1, w: 4 },
                      { x: an.x + 2, y: an.y + 2, w: 3 },
                    ]
                  : [4, 5, 6].map((w, r) => ({ x: an.x - 1, y: an.y + r, w }));
              rows.forEach((r, i) => px(g, r.x, r.y, r.w, 1, i === 2 ? c.skinS : c.skin));
            }
          },
        });
        const sh = j[`shoulder${s}`];
        const el = j[`elbow${s}`];
        const wrist = j[`wrist${s}`];
        const shape = handShape(sk, s, s === 'A' && armed);
        const tones = handTones(c.skin, c.skinS);
        let fineHand: HandSprite | undefined;
        const sprite = () => (fineHand ??= handSprite(sk, s, shape, tones, RIG_RES));
        const mittens = has('hands');
        /** The hanging mitten (straight rows, flush with the arm) for a relaxed hand; `handArt` for the rest. */
        const hanging = shape === 'relaxed' || shape === 'clasp';
        // Bare and turned, the near arm lies over the drawers' seat (drawn at the chest's depth + 0.011), so
        // the hanging arm and its hand stay in front of the hip instead of sinking behind the waistband.
        const other = j[`shoulder${s === 'A' ? 'B' : 'A'}`];
        const armZ = () =>
          isBare() && mittens && !sk.square && sh.z >= other.z
            ? Math.max(zOf(sk, `shoulder${s}`), zOf(sk, 'chest', 0.02))
            : zOf(sk, `shoulder${s}`);
        out.push({
          get z() {
            return armZ();
          },
          draw: (g) => {
            if (has('shoulders')) deltoid(g, sk, s, c, L);
            limb(
              g,
              armParts(sh, el, wrist, L, has('limbs'), mittens),
              c.skin,
              c.skinS,
              mittens ? armOuter(sk, s) : outerSide(sh.x - j.chest.x),
              true,
            );
          },
        });
        const drawNew = (g: G) => {
          if (forearmHidden(sk, s, armZ())) return;
          if (hanging) mitten(g, sk, s, c);
          else handArt(g, sk, s, shape, c);
        };
        out.push(
          mittens
            ? {
                get z() {
                  return armZ() + 0.08;
                },
                draw: drawNew,
                // A hand closed round a weapon is laid back over the hilt at full resolution (realism.ts), drawn
                // here as the same art pixels enlarged.
                ...(shape === 'grip' && {
                  grip: true,
                  hand: true,
                  fine: (g: G, res: number) => {
                    g.save();
                    g.scale(res, res);
                    drawNew(g);
                    g.restore();
                  },
                }),
              }
            : {
                z: zOf(sk, `shoulder${s}`, 0.08),
                grip: shape === 'grip',
                draw: (g) => drawHandArt(g, sprite(), RIG_RES),
                hand: true,
                fine: (g: G, res: number) =>
                  drawHand(g, res === RIG_RES ? sprite() : handSprite(sk, s, shape, tones, res)),
              },
        );
      }
      out.push({
        z: zOf(sk, 'chest'),
        draw: (g) => {
          if (has('limbs') && sk.view === 'side') {
            torso(g, sk, c.skin, c.skinS, 0, 'waist');
            sideHips(g, sk, 0, c.skin, c.skinS);
          } else torso(g, sk, c.skin, c.skinS, 0);
          if (has('torso') && isBare()) torsoForm(g, sk, c);
        },
      });
      out.push({ z: zOf(sk, 'head'), draw: (g) => head(g, sk, c) });
      return out;
    },
  };
}

/** Skeletons whose figure is bare (the default drawers or briefs are its only clothes), marked by those layers. */
const BARE = new WeakSet<Skeleton>();

/** Every fix of the approved body. */
const ALL: readonly LabFix[] = [
  'anatomy',
  'shoulders',
  'limbs',
  'feet',
  'torso',
  'stance',
  'hair',
  'hands',
  'shorts',
];

/** A rig pose on the new body: Model 0's proportions and the idle stance (`anatomy`, `labSkeleton`). */
export function newSkeleton(sk: Skeleton): Skeleton {
  return labSkeleton(sk, ALL);
}

/**
 * The bare body layer (figure.ts `bodyLayer`'s place): `armed` as `BodyLook.armed` (the weapon hand grips, always or
 * in the frames that carry the weapon).
 */
export function newBodyLayer(
  c: { skin: string; skinS: string },
  armed: boolean | ((sk: Skeleton) => boolean) = false,
): Layer {
  return labBodyLayer(c, ALL, false, armed);
}
